import { createRouter, createWebHistory, RouteRecordName, RouteRecordRaw } from 'vue-router'

import { Ory } from '@/plugins/ory'
import { CustomerPermissionsStore } from '@/stores/customerPermissionsStore'
import { getSanitizedReturnLocation, getWebsiteHost } from '@/helpers/returnUrlHelper'
import { useGlobalStore } from '@/stores/global-store'
import { UserActionsStore } from '@/stores/userActionsStore'
import type { UserActionKey } from '@/types/userActions/userActionCatalog'

const ORY_BASE_PATH = import.meta.env.VITE_APP_ORY_URL

const returnToUrl = getWebsiteHost()

export const routesListConfig: Record<string, any> = {
  dashboard: {
    name: 'dashboard',
    path: 'dashboard',
    component: () => import('../pages/admin/dashboard/Dashboard.vue'),
    customerPrivileges: ['SimManagement', 'ProductManagement'],
    meta: { requiresAuth: true },
  },
  invoices: {
    name: 'invoices',
    path: '/administration/invoices',
    component: () => import('../pages/administration/invoices/InvoicesPage.vue'),
    customerPrivileges: ['InvoiceManagement'],
    meta: { requiresAuth: true },
  },
  mindinvoices: {
    name: 'mindinvoices',
    path: '/administration/mind/mindinvoices',
    component: () => import('../pages/administration/legacyinvoices/InvoicesPage.vue'),
    customerPrivileges: ['InvoiceMindManagement'],
    meta: { requiresAuth: true },
  },
  internetDetail: {
    name: 'internetDetail',
    path: '/products/internet/:id',
    component: () => import('@/pages/products/internet/InternetDetailView.vue'),
    customerPrivileges: ['ProductManagement'],
    meta: { requiresAuth: true },
  },
  usagetable: {
    name: 'usagetable',
    path: 'usagetable',
    component: () => import('../pages/wakanda/UsageTable.vue'),
    customerPrivileges: ['SimManagement', 'ProductManagement'],
    meta: { requiresAuth: true },
  },
  orders: {
    name: 'orders',
    path: '/administration/orders',
    component: () => import('../pages/administration/orders/OrderView.vue'),
    customerPrivileges: ['OrderManagement'],
    meta: { requiresAuth: true },
  },
  detailOrder: {
    name: 'detailOrder',
    path: '/administration/orders/:id',
    component: () => import('@/pages/administration/orders/OrderDetailView.vue'),
    customerPrivileges: ['OrderManagement'],
    meta: { requiresAuth: true },
  },
  internet: {
    name: 'internet',
    path: '/products/internet',
    component: () => import('@/pages/products/internet/InternetView.vue'),
    customerPrivileges: ['ProductManagement'],
    meta: { requiresAuth: true },
  },
  detailMobile: {
    name: 'detailMobile',
    path: '/products/mobile/:id',
    component: () => import('@/pages/products/mobile/MobileDetailView.vue'),
    customerPrivileges: ['ProductManagement', 'SimManagement'],
    meta: { requiresAuth: true },
  },
  mobile: {
    name: 'mobile',
    path: '/products/mobile',
    component: () => import('@/pages/products/mobile/MobileView.vue'),
    customerPrivileges: ['SimManagement', 'ProductManagement'],
    meta: { requiresAuth: true },
  },
  support: {
    name: 'support',
    path: '/support',
    component: () => import('@/pages/support/SupportPage.vue'),
    customerPrivileges: ['SupportManagement'],
    meta: { requiresAuth: true },
  },
  usermanagement: {
    name: 'usermanagement',
    path: '/usermanagement',
    component: () => import('@/pages/userManagement/userManagementOverviewPage.vue'),
    customerPrivileges: ['UserManagement'],
    meta: { requiresAuth: true },
  },
  viewTicket: {
    name: 'viewTicket',
    path: '/support/:id',
    component: () => import('@/pages/support/SupportDetailPage.vue'),
    customerPrivileges: ['SupportManagement'],
    meta: { requiresAuth: true },
  },
  usage: {
    name: 'usage',
    path: '/usage/:id?',
    component: () => import('@/pages/Usage/UsageView.vue'),
    customerPrivileges: ['SimManagement', 'ProductManagement'],
    meta: { requiresAuth: true },
  },
  partnerdeals: {
    name: 'partnerdeals',
    path: '/partner/deals',
    customerPrivileges: ['DealManagement'],
    userActions: ['my_deals'],
    component: () => import('@/pages/partner/deals/dealsPage.vue'),
    meta: { requiresAuth: true },
  },
  partnerdocuments: {
    name: 'partnerdocuments',
    path: '/partner/documents',
    component: () => import('@/pages/partner/documents/DocumentPage.vue'),
    meta: { requiresAuth: true },
  },
  partnercommissions: {
    name: 'partnercommissions',
    path: '/partner/commissions',
    customerPrivileges: ['CommissionManagement'],
    userActions: ['my_commissions'],
    component: () => import('@/pages/partner/commissions/CommissionPage.vue'),
    meta: { requiresAuth: true },
  },
  partneragreements: {
    name: 'partneragreements',
    path: '/partner/agreements',
    customerPrivileges: ['AgreementManagement'],
    component: () => import('@/pages/partner/agreements/AgreementsComponent.vue'),
    meta: { requiresAuth: true },
  },
  partnertools: {
    name: 'partnertools',
    path: '/partner/tools',
    customerPrivileges: ['PresalesManagement'],
    userActions: ['partner_tools'],
    component: () => import('@/pages/partner/tools/ToolsPage.vue'),
    meta: { requiresAuth: true },
  },
  unauthorized: {
    name: 'unauthorized',
    path: '/unauthorized',
    component: () => import('@/pages/unauthorized.vue'),
  },
  '404': {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  Forbidden: {
    name: 'Forbidden',
    path: '/403',
    component: () => import('../pages/403.vue'),
  },
  accessCheck: {
    name: 'accessCheck',
    path: '/accessCheck',
    component: () => import('../pages/accessCheck/AccessCheck.vue'),
    meta: { requiresAuth: true },
  },
  unknown: {
    path: '/:pathMatch(.*)*',
    redirect: { name: '404' },
  },
  index: {
    name: 'index',
    path: '/',
    meta: {
      requiresAuth: true,
    },
    component: () => import('../layouts/AppLayout.vue'),
  },
  customerSelect: {
    name: 'customerSelect',
    path: '/customerSelect',
    meta: {
      requiresAuth: true,
    },
    component: () => import('../pages/customerSelect/customerSelect.vue'),
  },
}

const routes: Array<RouteRecordRaw> = [
  routesListConfig['unknown'],
  {
    ...routesListConfig['index'],
    children: [
      routesListConfig['dashboard'],
      routesListConfig['invoices'],
      routesListConfig['mindinvoices'],
      routesListConfig['internetDetail'],
      routesListConfig['usagetable'],
      routesListConfig['orders'],
      routesListConfig['detailOrder'],
      routesListConfig['internet'],
      routesListConfig['detailMobile'],
      routesListConfig['mobile'],
      routesListConfig['support'],
      routesListConfig['usermanagement'],
      routesListConfig['partnerdeals'],
      routesListConfig['partnerdocuments'],
      routesListConfig['partnercommissions'],
      routesListConfig['partnertools'],
      routesListConfig['partneragreements'],
      routesListConfig['viewTicket'],
      routesListConfig['usage'],
      routesListConfig['unauthorized'],
    ],
  },
  routesListConfig['404'],
  routesListConfig['Forbidden'],
  routesListConfig['customerSelect'],
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

function getReturnUrl() {
  const params = new URLSearchParams(window.location.search)
  const url = params.get('returnUrl') || window.location.href
  const returnUrl = new URL(url)
  return getSanitizedReturnLocation(returnUrl.toString())
}

const hasRoutePermission = (routeName?: RouteRecordName) => {
  if (!routeName) return false
  const customerPermissionsStore = CustomerPermissionsStore()
  const userActionsStore = UserActionsStore()
  const customerId = customerPermissionsStore.selectedCustomerId
  const customerPermissions = customerPermissionsStore.getCustomerPermission(customerId)
  const routeConfig = routesListConfig[routeName.toString()]

  const configPrivileges = routeConfig.customerPrivileges as Array<string>
  const hasPrivilege =
    !configPrivileges ||
    configPrivileges.length == 0 ||
    customerPermissions?.privileges.some((item) => configPrivileges.includes(item))

  const configUserActions = routeConfig.userActions as Array<UserActionKey> | undefined
  const hasUserAction =
    !configUserActions || configUserActions.length == 0 || configUserActions.some((key) => userActionsStore.can(key))

  return hasPrivilege && hasUserAction
}

router.beforeEach(async (to, from, next) => {
  const customerPermissionsStore = CustomerPermissionsStore()
  const userActionsStore = UserActionsStore()
  const globalStore = useGlobalStore()

  // Session & Auth Check
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    try {
      const { data: session } = await Ory.toSession()

      // Safety Guard: a new Ory session id (account recovery) or a different identity
      const isNewSession =
        globalStore.orySession?.id !== session?.id || globalStore.orySession?.identity?.id !== session?.identity?.id

      if (isNewSession) {
        globalStore.setCustomerSelectFlowFinished(false)
      }

      globalStore.setOrySession(session)

      // Fetch/Refresh Permissions
      // We pass isNewSession (force) to ensure if the user switched tabs/accounts,
      // the permissions are wiped and reloaded immediately.
      await customerPermissionsStore.refresh(isNewSession)

      const hasPermissions = customerPermissionsStore.customerPermissions.length > 0
      const selectionMade = !!(
        customerPermissionsStore.selectedCustomerId || customerPermissionsStore.selectedRelationContactId
      )

      // Unprivileged / empty IAM result must not land on `/` (empty AppLayout)
      if (!hasPermissions) {
        return next({ name: 'Forbidden', replace: true })
      }

      // If user has options but hasn't picked one, and isn't already on the selection page
      if (!selectionMade && to.name !== 'customerSelect') {
        return next({
          name: 'customerSelect',
          query: { returnUrl: to.fullPath }, // Optional: save where they were going
        })
      }

      // Validation: If a selection WAS made, ensure it still exists in the fresh permissions
      if (selectionMade) {
        const currentId =
          customerPermissionsStore.selectedCustomerId || customerPermissionsStore.selectedRelationContactId
        const stillHasAccess = customerPermissionsStore.customerPermissions.some((p) => p.id === currentId)

        if (!stillHasAccess && to.name !== 'customerSelect') {
          customerPermissionsStore.clearStore() // Wipe stale selection
          userActionsStore.clearUserActions()
          return next({ name: 'customerSelect' })
        }
      }
    } catch (error: any) {
      // If session is missing or expired
      // Reactive AAL2 CHECK (Handles 403 Forbidden)
      if (error.response?.status === 403) {
        window.location.href = `${ORY_BASE_PATH}/login?aal=aal2&return_to=${encodeURIComponent(returnToUrl)}`
      } else {
        window.location.href = `${ORY_BASE_PATH}/login?return_to=${encodeURIComponent(returnToUrl)}`
      }
      return
    }
  }

  // We check for Mind users only after we are sure refresh() finished
  const isMindUser = customerPermissionsStore.customerPermissions.some((x) => x.system === 'mind')

  if (isMindUser && to.name === routesListConfig.invoices.name) {
    return next({
      name: routesListConfig.mindinvoices.name,
      query: to.query,
    })
  }

  // Permission Authorization
  if (customerPermissionsStore.permissionsLoaded) {
    // Check if the current user has the rights for this specific route
    if (to.matched.some((record) => !hasRoutePermission(record.name))) {
      return next({
        path: '/unauthorized',
        query: { returnUrl: getReturnUrl() },
        replace: true,
      })
    }
  }

  next()
})
export default router
